import { useState, useEffect } from 'react'
import { useGameStore } from '@store/gameStore'
import './SmsIntroScreen.css'
import './HomeScreenScreen.css'

// App icons matching HomeScreenScreen
const APP_ICONS = [
    { id: 'messages', emoji: '💬', name: 'Nachrichten', color: '#34c759', hasBadge: true },
    { id: 'phone', emoji: '📞', name: 'Telefon', color: '#34c759' },
    { id: 'safari', emoji: '🧭', name: 'Safari', color: '#007aff' },
    { id: 'mail', emoji: '📧', name: 'Mail', color: '#007aff' },
    { id: 'music', emoji: '🎵', name: 'Musik', color: '#fc3c44' },
    { id: 'photos', emoji: '🌈', name: 'Fotos', color: '#fff' },
    { id: 'camera', emoji: '📷', name: 'Kamera', color: '#8e8e93' },
    { id: 'settings', emoji: '⚙️', name: 'Einstellungen', color: '#8e8e93' },
    { id: 'appstore', emoji: '🅰️', name: 'App Store', color: '#007aff' },
    { id: 'calendar', emoji: '📅', name: 'Kalender', color: '#ff3b30' },
    { id: 'clock', emoji: '🕐', name: 'Uhr', color: '#000' },
    { id: 'weather', emoji: '⛅', name: 'Wetter', color: '#5ac8fa' },
    { id: 'maps', emoji: '🗺️', name: 'Karten', color: '#34c759' },
    { id: 'notes', emoji: '📝', name: 'Notizen', color: '#ffcc00' },
    { id: 'pay2lose', emoji: '', name: 'Pay2Lose', color: '#000', isTarget: true },
    { id: 'health', emoji: '❤️', name: 'Health', color: '#ff2d55' },
    { id: 'wallet', emoji: '💳', name: 'Wallet', color: '#000' },
    { id: 'files', emoji: '📁', name: 'Dateien', color: '#007aff' },
    { id: 'reminders', emoji: '📋', name: 'Erinnerungen', color: '#007aff' },
    { id: 'news', emoji: '📰', name: 'News', color: '#fc3c44' },
]

const DOCK_APPS = [
    { id: 'phone', emoji: '📞', name: 'Telefon', color: '#34c759' },
    { id: 'safari', emoji: '🧭', name: 'Safari', color: '#007aff' },
    { id: 'messages', emoji: '💬', name: 'Nachrichten', color: '#34c759', hasBadge: true },
    { id: 'music', emoji: '🎵', name: 'Musik', color: '#fc3c44' },
]

// Phase type for the complete intro flow
type IntroPhase =
    | 'homescreen-init'      // Show home screen
    | 'homescreen-badge'     // Highlight messages with badge
    | 'opening-messages'     // Opening messages app animation
    | 'messages-typing'      // In messages app, showing typing indicator
    | 'messages-text'        // Typing out the message
    | 'messages-notification' // Bank notification
    | 'closing-messages'     // Closing messages app
    | 'done'                 // Transition complete

export function SmsIntroScreen() {
    const setScreen = useGameStore(state => state.setScreen)
    const addEuro = useGameStore(state => state.addEuro)

    const [phase, setPhase] = useState<IntroPhase>('homescreen-init')
    const [displayedText, setDisplayedText] = useState('')
    const [showNotification, setShowNotification] = useState(false)
    const [isClosingMessages, setIsClosingMessages] = useState(false)
    const [showMessagesApp, setShowMessagesApp] = useState(false)

    const fullMessage = "Hier sind 50€ Taschengeld für dich, mein Schatz! 💶 Aber gib nicht alles auf einmal aus! 😊"

    // Get current time for status bar
    const currentTime = new Date().toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
    })

    // Phase 1: Show home screen, then highlight messages badge
    useEffect(() => {
        if (phase !== 'homescreen-init') return

        const timer = setTimeout(() => {
            setPhase('homescreen-badge')
        }, 2000) // Wait 2 seconds on home screen

        return () => clearTimeout(timer)
    }, [phase])

    // Phase 2: Messages badge highlighted, then open messages
    useEffect(() => {
        if (phase !== 'homescreen-badge') return

        const timer = setTimeout(() => {
            setPhase('opening-messages')
        }, 2000) // Badge visible for 2 seconds

        return () => clearTimeout(timer)
    }, [phase])

    // Phase 3: Opening messages animation
    useEffect(() => {
        if (phase !== 'opening-messages') return

        const timer = setTimeout(() => {
            setShowMessagesApp(true)
            setPhase('messages-typing')
        }, 600) // Animation duration

        return () => clearTimeout(timer)
    }, [phase])

    // Phase 4: Typing indicator visible
    useEffect(() => {
        if (phase !== 'messages-typing') return

        const timer = setTimeout(() => {
            setPhase('messages-text')
        }, 2500) // Typing indicator for 2.5 seconds

        return () => clearTimeout(timer)
    }, [phase])

    // Phase 5: Type out the message character by character
    useEffect(() => {
        if (phase !== 'messages-text') return

        const messageChars = [...fullMessage]
        if (displayedText.length >= fullMessage.length) {
            const timer = setTimeout(() => {
                setShowNotification(true)
                setPhase('messages-notification')
            }, 1500) // Pause after message complete
            return () => clearTimeout(timer)
        }

        const timer = setTimeout(() => {
            // Get the next "character" (could be a multi-byte emoji)
            const currentChars = [...displayedText]
            const nextChar = messageChars[currentChars.length]
            setDisplayedText(displayedText + nextChar)
        }, 50) // Slower typing

        return () => clearTimeout(timer)
    }, [phase, displayedText])

    // Phase 6: Notification visible, then hide and close
    useEffect(() => {
        if (phase !== 'messages-notification') return

        const hideNotificationTimer = setTimeout(() => {
            setShowNotification(false)
        }, 3000)

        const closeTimer = setTimeout(() => {
            setIsClosingMessages(true)
            setPhase('closing-messages')
        }, 4000)

        return () => {
            clearTimeout(hideNotificationTimer)
            clearTimeout(closeTimer)
        }
    }, [phase])

    // Phase 7: Closing animation, then go to home screen for Pay2Lose
    useEffect(() => {
        if (phase !== 'closing-messages') return

        const timer = setTimeout(() => {
            addEuro(50)
            setScreen('homescreen')
        }, 800)

        return () => clearTimeout(timer)
    }, [phase, setScreen, addEuro])

    // ========================================
    // RENDER HOME SCREEN (before messages app opens)
    // ========================================
    if (!showMessagesApp) {
        const isHighlighting = phase === 'homescreen-badge' || phase === 'opening-messages'

        return (
            <div className="home-screen">
                {/* iOS-style Status Bar */}
                <div className="home-screen__status-bar">
                    <span className="home-screen__time">{currentTime}</span>
                    <div className="home-screen__notch"></div>
                    <div className="home-screen__status-icons">
                        <span>📶</span>
                        <span>📡</span>
                        <span>🔋</span>
                    </div>
                </div>

                {/* App Grid */}
                <div className="home-screen__grid">
                    {APP_ICONS.map(app => (
                        <div
                            key={app.id}
                            className={`home-screen__app ${app.id === 'messages' && isHighlighting ? 'home-screen__app--highlight' : ''} ${app.isTarget ? 'home-screen__app--target' : ''}`}
                        >
                            <div className="home-screen__app-icon" style={{ background: app.color }}>
                                {app.isTarget ? (
                                    <img src="./appicon.png" alt="Pay2Lose" className="home-screen__app-img" />
                                ) : (
                                    <span className="home-screen__app-emoji">{app.emoji}</span>
                                )}
                                {app.hasBadge && (
                                    <span className="home-screen__app-badge">1</span>
                                )}
                            </div>
                            <span className="home-screen__app-name">{app.name}</span>
                        </div>
                    ))}
                </div>

                {/* Page Indicator Dots */}
                <div className="home-screen__page-dots">
                    <span className="home-screen__dot home-screen__dot--active"></span>
                    <span className="home-screen__dot"></span>
                    <span className="home-screen__dot"></span>
                </div>

                {/* Dock */}
                <div className="home-screen__dock">
                    {DOCK_APPS.map(app => (
                        <div key={app.id} className="home-screen__dock-app">
                            <div className="home-screen__app-icon" style={{ background: app.color }}>
                                <span className="home-screen__app-emoji">{app.emoji}</span>
                                {app.hasBadge && (
                                    <span className="home-screen__app-badge">1</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Home Indicator */}
                <div className="home-screen__home-indicator"></div>
            </div>
        )
    }

    // ========================================
    // RENDER MESSAGES APP
    // ========================================
    return (
        <div className={`sms-intro ${isClosingMessages ? 'sms-intro--closing' : ''}`}>
            {/* iOS-style Status Bar */}
            <div className="sms-intro__status-bar">
                <span className="sms-intro__time">{currentTime}</span>
                <div className="sms-intro__status-icons">
                    <span>📶</span>
                    <span>📡</span>
                    <span>🔋</span>
                </div>
            </div>

            {/* Notification Banner */}
            <div className={`sms-intro__notification ${showNotification ? 'sms-intro__notification--visible' : ''}`}>
                <div className="sms-intro__notification-icon">🏦</div>
                <div className="sms-intro__notification-content">
                    <span className="sms-intro__notification-app">Sparkasse</span>
                    <span className="sms-intro__notification-text">+50,00€ Überweisung von Oma erhalten</span>
                </div>
            </div>

            {/* Messages App Header */}
            <div className="sms-intro__header">
                <button className="sms-intro__back-btn">‹ Zurück</button>
                <div className="sms-intro__contact">
                    <img src="./oma-avatar.png" alt="Oma" className="sms-intro__avatar" />
                    <span className="sms-intro__contact-name">Oma ❤️</span>
                </div>
                <div className="sms-intro__header-spacer"></div>
            </div>

            {/* Chat Area */}
            <div className="sms-intro__chat">
                {/* Date Header */}
                <div className="sms-intro__date">Heute</div>

                {/* Typing Indicator */}
                {phase === 'messages-typing' && (
                    <div className="sms-intro__bubble sms-intro__bubble--typing">
                        <div className="sms-intro__typing-dots">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                )}

                {/* Message Bubble */}
                {displayedText && (
                    <div className="sms-intro__bubble sms-intro__bubble--received">
                        <p className="sms-intro__message">{displayedText}</p>
                        {(phase === 'messages-notification' || phase === 'closing-messages') && (
                            <span className="sms-intro__timestamp">
                                {currentTime} ✓✓
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Input Area (decorative) */}
            <div className="sms-intro__input-area">
                <div className="sms-intro__input-field">
                    <span className="sms-intro__input-placeholder">iMessage</span>
                </div>
                <button className="sms-intro__send-btn">↑</button>
            </div>

            {/* Home Indicator */}
            <div className="sms-intro__home-indicator"></div>
        </div>
    )
}
